import { Link } from '@tanstack/react-router'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import AuthLayout from '../auth-layout'

export default function PrivacyPolicy() {
  return (
    <AuthLayout>
      <Card className='max-h-[85vh] overflow-hidden p-6'>
        <div className='mb-6'>
          <Link to='/sign-in'>
            <Button variant='ghost' size='sm' className='mb-4'>
              <ArrowLeft className='mr-2 h-4 w-4' />
              Back to Login
            </Button>
          </Link>
          <h1 className='text-2xl font-semibold tracking-tight'>
            Privacy Policy
          </h1>
          <p className='text-sm text-muted-foreground'>
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className='max-h-[calc(85vh-180px)] overflow-y-auto pr-2'>
          <div className='prose prose-sm max-w-none dark:prose-invert'>
          <h2>1. Introduction</h2>
          <p>
            Welcome to Menu Points Admin ("we", "our", or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
          </p>

          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We collect personal information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Register for an account</li>
            <li>Use the Service</li>
            <li>Contact us for support</li>
          </ul>
          <p>This information may include:</p>
          <ul>
            <li>Name and contact information (email, phone number)</li>
            <li>Business information (restaurant name, address)</li>
            <li>Account credentials (username, password)</li>
            <li>Payment information (processed securely through third-party providers)</li>
          </ul>

          <h3>2.2 Usage Data</h3>
          <p>We automatically collect certain information when you use the Service:</p>
          <ul>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage patterns (pages visited, features used, time spent)</li>
            <li>Log data (access times, error logs)</li>
          </ul>

          <h3>2.3 Menu Content</h3>
          <p>Content you upload to create your digital menu, including:</p>
          <ul>
            <li>Menu item names and descriptions</li>
            <li>Prices and categories</li>
            <li>Images and media files</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service</li>
            <li>Process your transactions and manage your account</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Analyze usage patterns to enhance user experience</li>
            <li>Detect and prevent fraud or abuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. Information Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
          
          <h3>4.1 Service Providers</h3>
          <p>
            We may share your information with third-party service providers who perform services on our behalf, such as:
          </p>
          <ul>
            <li>Cloud hosting providers</li>
            <li>Payment processors</li>
            <li>Analytics services</li>
            <li>Customer support tools</li>
          </ul>

          <h3>4.2 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities.</p>

          <h3>4.3 Business Transfers</h3>
          <p>
            If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
          <p>Security measures include:</p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication</li>
            <li>Secure data centers with redundancy</li>
          </ul>

          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
          </p>

          <h2>7. Your Privacy Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Request correction of inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service</li>
            <li><strong>Objection:</strong> Object to processing of your personal information</li>
            <li><strong>Restriction:</strong> Request restriction of processing</li>
          </ul>
          <p>To exercise these rights, please contact us at privacy@menupoints.com</p>

          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
          </p>

          <h2>9. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>

          <h2>10. Children's Privacy</h2>
          <p>
            Our Service is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.
          </p>

          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ.
          </p>

          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>

          <h2>13. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us:
            <br />
            Email: privacy@menupoints.com
            <br />
            Support: support@menupoints.com
          </p>
        </div>
        </div>
      </Card>
    </AuthLayout>
  )
}
